import React from 'react';
import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
  UnifiedThemeProvider,
} from '@backstage/theme';
import type { AppTheme } from '@backstage/core-plugin-api';
import DarkIcon from '@material-ui/icons/Brightness2';
import LightIcon from '@material-ui/icons/WbSunny';

declare module '@material-ui/core/styles/createPalette' {
  interface PaletteColor {
    announcementBackground: string;
  }
}

const baseFontFamily =
  '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif';
const headingFontFamily =
  '"Poppins", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif';

const spinByOxxoTheme = {
  light: {
    colors: {
      content_primary: '#141418',
      content_secondary: '#33333d',
      content_tertiary: '#71717f',
      content_primary_inverse: '#ffffff',
      content_secondary_inverse: '#a8a8b2',
      content_tertiary_inverse: '#71717f',
      content_accent: '#5600d9',
      surface_primary: '#ffffff',
      surface_secondary: '#f7f8fa',
      surface_tertiary: '#e2e2e9',
      surface_primary_inverse: '#131a38',
      surface_secondary_inverse: '#1a2756',
      surface_tertiary_inverse: '#283b8a',
      surface_primary_hover_inverse: '#151e44',
      surface_secondary_hover_inverse: '#1e2e6d',
      surface_primary_hover: '#f9f9ff',
      surface_secondary_hover: '#efeef5',
      stroke_primary: '#8b95b9',
      stroke_secondary: '#dbddef',
      stroke_secondary_inverse: '#dbddef',
      stroke_primary_inverse: '#ffffff',
      ui_active: '#5600d9',
      ui_success: '#3b8c04',
      ui_warn: '#cc8300',
      ui_error: '#cc3e3d',
      ui_active_inverse: '#9876de',
      ui_success_inverse: '#88ca47',
      ui_warn_inverse: '#ffc866',
      ui_error_inverse: '#ff7372',
      ui_disabled_inverse: '#a8a8b2',
      ui_active_hover_inverse: '#c367ef',
      ui_disabled: '#bebec6',
      ui_active_tonal: '#eee8fa',
      ui_skeleton: '#f5f5f7',
      ui_active_hover: '#4216a0',
      ui_active_tonal_hover: '#ddd1f4',
      ui_focus_outer: '#baa4e9',
      ui_focus_inner: '#4216a0',
      informational_surface: '#cfe4f8',
      informational_decoration: '#3d93e2',
      informational_content: '#084883',
      paused_surface: '#ffedcc',
      paused_decoration: '#ffb633',
      paused_content: '#664200',
      success_surface: '#e2fcbc',
      success_decoration: '#45a206',
      success_content: '#2c6903',
      urgent_surface: '#ffc7c7',
      urgent_decoration: '#ed5150',
      urgent_content: '#661d1c',
      brand_primary: '#5600d9',
      brand_secondary: '#ff6700',
      brand_tertiary: '#210b50',
      highlight_surface: '#fedaba',
      highlight_decoration: '#fc9e54',
      highlight_content: '#993300',
      highlight_secondary_surface: '#ddd1f4',
      highlight_secondary_decoration: '#7549d3',
      highlight_secondary_content: '#4216a0',
      points_surface: '#cdf3f8',
      points_decoration: '#37cfe4',
      points_content: '#024e58',
      supportive_space: '#210b50',
      supportive_lilac: '#c7a1ff',
      supportive_bloom: '#eee3ff',
      supportive_brick: '#542200',
      supportive_peach: '#ffa366',
      supportive_sand: '#fff0e5',
      supportive_comet: '#2d2f3a',
      supportive_cloud: '#d9dbfd',
      brand_gradient: ['#531bc8', '#7549d3'],
    },
  },
  dark: {
    colors: {
      content_primary: '#ffffff',
      content_secondary: '#d6d8e8',
      content_tertiary: '#a8a8b2',
      content_primary_inverse: '#ffffff',
      content_secondary_inverse: '#d6d8e8',
      content_tertiary_inverse: '#a8a8b2',
      content_accent: '#c7a1ff',
      surface_primary: '#1a2756',
      surface_secondary: '#131a38',
      surface_tertiary: '#283b8a',
      surface_primary_inverse: '#131a38',
      surface_secondary_inverse: '#1a2756',
      surface_tertiary_inverse: '#283b8a',
      surface_primary_hover_inverse: '#151e44',
      surface_secondary_hover_inverse: '#1e2e6d',
      surface_primary_hover: '#1e2e6d',
      surface_secondary_hover: '#151e44',
      stroke_primary: '#9aa7d6',
      stroke_secondary: '#3f4f84',
      stroke_secondary_inverse: '#3f4f84',
      stroke_primary_inverse: '#ffffff',
      ui_active: '#c7a1ff',
      ui_success: '#88ca47',
      ui_warn: '#ffc866',
      ui_error: '#ff7372',
      ui_active_inverse: '#c7a1ff',
      ui_success_inverse: '#88ca47',
      ui_warn_inverse: '#ffc866',
      ui_error_inverse: '#ff7372',
      ui_disabled_inverse: '#a8a8b2',
      ui_active_hover_inverse: '#c367ef',
      ui_disabled: '#71717f',
      ui_active_tonal: '#2f2356',
      ui_skeleton: '#283250',
      ui_active_hover: '#d6bdff',
      ui_active_tonal_hover: '#3a2b6f',
      ui_focus_outer: '#baa4e9',
      ui_focus_inner: '#d6bdff',
      informational_surface: '#163a5c',
      informational_decoration: '#4fc3f7',
      informational_content: '#cfe4f8',
      paused_surface: '#4a3415',
      paused_decoration: '#ffc866',
      paused_content: '#ffedcc',
      success_surface: '#254216',
      success_decoration: '#88ca47',
      success_content: '#e2fcbc',
      urgent_surface: '#4a2424',
      urgent_decoration: '#ff7372',
      urgent_content: '#ffc7c7',
      brand_primary: '#9876de',
      brand_secondary: '#ff6700',
      brand_tertiary: '#eee3ff',
      highlight_surface: '#4a321f',
      highlight_decoration: '#fc9e54',
      highlight_content: '#fedaba',
      highlight_secondary_surface: '#2f2356',
      highlight_secondary_decoration: '#c7a1ff',
      highlight_secondary_content: '#eee3ff',
      points_surface: '#164049',
      points_decoration: '#37cfe4',
      points_content: '#cdf3f8',
      supportive_space: '#210b50',
      supportive_lilac: '#c7a1ff',
      supportive_bloom: '#eee3ff',
      supportive_brick: '#542200',
      supportive_peach: '#ffa366',
      supportive_sand: '#fff0e5',
      supportive_comet: '#2d2f3a',
      supportive_cloud: '#d9dbfd',
      brand_gradient: ['#531bc8', '#7549d3'],
    },
  },
} as const;

type SpinThemeMode = keyof typeof spinByOxxoTheme;

const spinTypography = {
  htmlFontSize: 16,
  fontFamily: baseFontFamily,
  h1: {
    fontFamily: headingFontFamily,
    fontSize: 40,
    fontWeight: 600,
    marginBottom: 10,
  },
  h2: {
    fontFamily: headingFontFamily,
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 8,
  },
  h3: {
    fontFamily: headingFontFamily,
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 6,
  },
  h4: {
    fontFamily: headingFontFamily,
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 6,
  },
  h5: {
    fontFamily: headingFontFamily,
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 4,
  },
  h6: {
    fontFamily: headingFontFamily,
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 2,
  },
};

const spinComponents = {
  MuiTextField: {
    defaultProps: {
      variant: 'outlined' as const,
      margin: 'dense' as const,
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        minHeight: 44,
      },
      input: {
        padding: '13.5px 14px',
        lineHeight: 1.2,
      },
      inputMarginDense: {
        paddingTop: 13.5,
        paddingBottom: 13.5,
      },
      inputMultiline: {
        padding: 0,
      },
      multiline: {
        padding: '13.5px 14px',
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      outlined: {
        transform: 'translate(14px, 14px) scale(1)',
        '&.MuiInputLabel-marginDense': {
          transform: 'translate(14px, 14px) scale(1)',
        },
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -6px) scale(0.75)',
        },
      },
      shrink: {
        transform: 'translate(14px, -6px) scale(0.75)',
      },
    },
  },
};

const spinPageTheme = {
  home: genPageTheme({
    colors: spinByOxxoTheme.light.colors.brand_gradient,
    shape: shapes.wave,
  }),
  documentation: genPageTheme({
    colors: [
      spinByOxxoTheme.light.colors.informational_content,
      spinByOxxoTheme.light.colors.informational_decoration,
    ],
    shape: shapes.wave2,
  }),
  tool: genPageTheme({
    colors: [
      spinByOxxoTheme.light.colors.highlight_secondary_content,
      spinByOxxoTheme.light.colors.highlight_secondary_decoration,
    ],
    shape: shapes.round,
  }),
  service: genPageTheme({
    colors: [
      spinByOxxoTheme.light.colors.brand_tertiary,
      spinByOxxoTheme.light.colors.brand_primary,
    ],
    shape: shapes.wave,
  }),
  website: genPageTheme({
    colors: [
      spinByOxxoTheme.light.colors.ui_active,
      spinByOxxoTheme.light.colors.ui_active_inverse,
    ],
    shape: shapes.wave,
  }),
  library: genPageTheme({
    colors: [
      spinByOxxoTheme.light.colors.urgent_content,
      spinByOxxoTheme.light.colors.urgent_decoration,
    ],
    shape: shapes.wave,
  }),
  other: genPageTheme({
    colors: [
      spinByOxxoTheme.light.colors.supportive_comet,
      spinByOxxoTheme.light.colors.content_tertiary,
    ],
    shape: shapes.wave,
  }),
  app: genPageTheme({
    colors: [
      spinByOxxoTheme.light.colors.brand_secondary,
      spinByOxxoTheme.light.colors.highlight_decoration,
    ],
    shape: shapes.wave,
  }),
  apis: genPageTheme({
    colors: [
      spinByOxxoTheme.light.colors.points_content,
      spinByOxxoTheme.light.colors.points_decoration,
    ],
    shape: shapes.wave2,
  }),
  card: genPageTheme({
    colors: [
      spinByOxxoTheme.light.colors.success_content,
      spinByOxxoTheme.light.colors.success_decoration,
    ],
    shape: shapes.wave,
  }),
};

const createSpinPalette = (mode: SpinThemeMode) => {
  const colors = spinByOxxoTheme[mode].colors;
  const isDark = mode === 'dark';
  const basePalette = isDark ? palettes.dark : palettes.light;
  const background = isDark
    ? {
        default: colors.surface_primary_inverse,
        paper: colors.surface_secondary_inverse,
      }
    : {
        default: colors.surface_secondary,
        paper: colors.surface_primary,
      };
  const text = isDark
    ? {
        primary: colors.content_primary_inverse,
        secondary: colors.content_secondary_inverse,
        disabled: colors.content_tertiary_inverse,
      }
    : {
        primary: colors.content_primary,
        secondary: colors.content_secondary,
        disabled: colors.content_tertiary,
      };

  return {
    ...basePalette,
    type: mode,
    mode,
    primary: {
      main: colors.brand_primary,
      light: colors.ui_active_inverse,
      dark: colors.ui_active_hover,
      contrastText: colors.content_primary_inverse,
    },
    secondary: {
      main: colors.brand_secondary,
      light: colors.supportive_peach,
      dark: colors.highlight_content,
      contrastText: colors.content_primary_inverse,
    },
    error: {
      main: colors.ui_error,
      light: colors.urgent_decoration,
      dark: colors.urgent_content,
      contrastText: colors.content_primary_inverse,
      announcementBackground: isDark ? '#4a2424' : '#ffe3e3',
    },
    warning: {
      main: colors.ui_warn,
      light: colors.paused_decoration,
      dark: colors.paused_content,
      contrastText: colors.content_primary_inverse,
      announcementBackground: isDark ? '#4a3415' : '#fff5e6',
    },
    success: {
      main: colors.ui_success,
      light: colors.success_decoration,
      dark: colors.success_content,
      contrastText: colors.content_primary_inverse,
      announcementBackground: isDark ? '#254216' : '#f0fde0',
    },
    info: {
      main: colors.informational_content,
      light: colors.informational_decoration,
      dark: colors.points_content,
      contrastText: colors.content_primary_inverse,
      announcementBackground: isDark ? '#163a5c' : '#e8f2fc',
    },
    background,
    text,
    status: {
      ok: colors.ui_success,
      warning: colors.ui_warn,
      error: colors.ui_error,
      pending: colors.paused_decoration,
      running: colors.informational_content,
      aborted: colors.ui_disabled,
    },
    border: isDark ? colors.stroke_secondary_inverse : colors.stroke_secondary,
    textContrast: isDark
      ? colors.content_primary_inverse
      : colors.content_primary,
    textVerySubtle: text.disabled,
    textSubtle: text.secondary,
    highlight: colors.highlight_surface,
    errorBackground: colors.urgent_surface,
    warningBackground: colors.paused_surface,
    infoBackground: colors.informational_surface,
    successBackground: colors.success_surface,
    errorText: colors.ui_error,
    infoText: colors.informational_content,
    warningText: colors.ui_warn,
    linkHover: colors.ui_active_hover,
    link: colors.ui_active,
    gold: colors.paused_decoration,
    navigation: {
      background: colors.surface_primary_inverse,
      indicator: colors.brand_secondary,
      color: colors.content_secondary_inverse,
      selectedColor: colors.content_primary_inverse,
      navItem: {
        hoverBackground: colors.surface_primary_hover_inverse,
      },
      submenu: {
        background: colors.surface_secondary_inverse,
      },
    },
    tabbar: {
      indicator: colors.brand_secondary,
    },
    pinSidebarButton: {
      icon: colors.surface_primary_inverse,
      background: colors.content_secondary_inverse,
    },
    banner: {
      info: colors.informational_content,
      error: colors.ui_error,
      warning: colors.ui_warn,
      text: colors.content_primary_inverse,
      link: colors.content_primary_inverse,
      closeButtonColor: colors.content_primary_inverse,
    },
    bursts: {
      fontColor: colors.content_primary_inverse,
      slackChannelText: colors.content_secondary_inverse,
      backgroundColor: {
        default: colors.brand_primary,
      },
      gradient: {
        linear: `linear-gradient(90deg, ${colors.brand_gradient.join(', ')})`,
      },
    },
    code: {
      background: isDark
        ? colors.surface_tertiary_inverse
        : colors.surface_tertiary,
    },
  };
};

export const spinLightTheme = createUnifiedTheme({
  palette: createSpinPalette('light'),
  defaultPageTheme: 'home',
  pageTheme: spinPageTheme,
  fontFamily: baseFontFamily,
  typography: spinTypography,
  components: spinComponents,
});

export const spinDarkTheme = createUnifiedTheme({
  palette: createSpinPalette('dark'),
  defaultPageTheme: 'home',
  pageTheme: spinPageTheme,
  fontFamily: baseFontFamily,
  typography: spinTypography,
  components: spinComponents,
});

export const spinAppThemes: AppTheme[] = [
  {
    id: 'light',
    title: 'Light Theme',
    variant: 'light',
    icon: React.createElement(LightIcon),
    Provider: ({ children }) =>
      React.createElement(UnifiedThemeProvider, {
        children,
        theme: spinLightTheme,
        themeName: 'spin-light',
      }),
  },
  {
    id: 'dark',
    title: 'Dark Theme',
    variant: 'dark',
    icon: React.createElement(DarkIcon),
    Provider: ({ children }) =>
      React.createElement(UnifiedThemeProvider, {
        children,
        theme: spinDarkTheme,
        themeName: 'spin-dark',
      }),
  },
];
